import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { money, currentPeriod, shortDate, periodLabel } from "@/lib/format";
import { WebhookBanner } from "@/components/layout/AdminShell";
import { useAuth } from "@/context/AuthContext";
import { TrendingUp, Users2, Clock3, DollarSign, ArrowUpRight, ArrowDownRight, DownloadCloud, SearchCheck, ShieldCheck, X, AlertTriangle, PackageSearch, GripVertical, RotateCcw, EyeOff, Eye, CheckCircle2, ClipboardCheck, MoreHorizontal, Rocket } from "lucide-react";
import ProductBreakdown from "@/components/ProductBreakdown";
import OutstandingPayoutsBanner from "@/components/OutstandingPayoutsBanner";
import EmailHealthPill from "@/components/EmailHealthPill";
import InboundHealthCard from "@/components/InboundHealthCard";
import ReconcilePreviewModal from "@/components/ReconcilePreviewModal";
import LedgerHealthStrip from "@/components/LedgerHealthStrip";
import FinaliseMonthWizard from "@/components/FinaliseMonthWizard";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const DEFAULT_LAYOUT = ["trend_top", "leaderboard", "products", "coverage", "webhooks"];
const LAYOUT_STORAGE_PREFIX = "sc_admin_overview_layout_v1_";
const HIDDEN_STORAGE_PREFIX = "sc_admin_overview_hidden_v1_";

export default function AdminOverview() {
  const [period, setPeriod] = useState(currentPeriod());
  const [data, setData] = useState(null);
  const [config, setConfig] = useState(null);
  const [products, setProducts] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backfilling, setBackfilling] = useState(false);
  const [auditing, setAuditing] = useState(false);
  const [audit, setAudit] = useState(null);
  const [integrity, setIntegrity] = useState(null);
  const [integrityBusy, setIntegrityBusy] = useState(false);
  const [dedupeBusy, setDedupeBusy] = useState(false);
  const [bundledBusy, setBundledBusy] = useState(false);
  const [bundledScan, setBundledScan] = useState(null);
  const [showReconcile, setShowReconcile] = useState(false);
  const [showFinalise, setShowFinalise] = useState(false);
  const navigate = useNavigate();
  const { state } = useAuth();

  // Per-admin card ordering, persisted to localStorage. New sections that
  // aren't in the saved order get appended so future additions don't get lost.
  const storageKey = state?.user?.email
    ? `${LAYOUT_STORAGE_PREFIX}${state.user.email.toLowerCase()}`
    : `${LAYOUT_STORAGE_PREFIX}_anon`;
  const hiddenKey = state?.user?.email
    ? `${HIDDEN_STORAGE_PREFIX}${state.user.email.toLowerCase()}`
    : `${HIDDEN_STORAGE_PREFIX}_anon`;
  const [layout, setLayout] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return DEFAULT_LAYOUT;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return DEFAULT_LAYOUT;
      const filtered = parsed.filter((k) => DEFAULT_LAYOUT.includes(k));
      const missing = DEFAULT_LAYOUT.filter((k) => !filtered.includes(k));
      return [...filtered, ...missing];
    } catch (_err) {
      return DEFAULT_LAYOUT;
    }
  });
  const [hidden, setHidden] = useState(() => {
    try {
      const raw = localStorage.getItem(hiddenKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter((k) => DEFAULT_LAYOUT.includes(k)) : [];
    } catch (_err) {
      return [];
    }
  });
  const [dragKey, setDragKey] = useState(null);
  const [overKey, setOverKey] = useState(null);

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(layout));
    } catch (_err) { /* private-mode Safari */ }
  }, [layout, storageKey]);
  useEffect(() => {
    try {
      localStorage.setItem(hiddenKey, JSON.stringify(hidden));
    } catch (_err) { /* private-mode Safari */ }
  }, [hidden, hiddenKey]);

  function hideSection(key) {
    setHidden((prev) => (prev.includes(key) ? prev : [...prev, key]));
  }
  function showSection(key) {
    setHidden((prev) => prev.filter((k) => k !== key));
  }

  function onDragStart(key) {
    return (e) => {
      setDragKey(key);
      try { e.dataTransfer.effectAllowed = "move"; } catch (_err) { /* older browsers ignore */ }
    };
  }
  function onDragOver(key) {
    return (e) => {
      e.preventDefault();
      if (key !== overKey) setOverKey(key);
    };
  }
  function onDrop(key) {
    return (e) => {
      e.preventDefault();
      if (!dragKey || dragKey === key) {
        setDragKey(null); setOverKey(null); return;
      }
      setLayout((prev) => {
        const next = [...prev];
        const from = next.indexOf(dragKey);
        const to = next.indexOf(key);
        if (from === -1 || to === -1) return prev;
        next.splice(from, 1);
        next.splice(to, 0, dragKey);
        return next;
      });
      setDragKey(null); setOverKey(null);
    };
  }
  function resetLayout() {
    setLayout(DEFAULT_LAYOUT);
    setHidden([]);
    toast.success("Layout reset to default");
  }

  async function reload() {
    setLoading(true);
    try {
      const [d, c, p, cov] = await Promise.all([
        api.get(`/commissions/dashboard/${period}`),
        api.get(`/commissions/config`),
        api.get(`/commissions/analytics/products`),
        api.get(`/commissions/rules/coverage`),
      ]);
      setData(d.data); setConfig(c.data); setProducts(p.data); setCoverage(cov.data);
    } catch (_e) {
      toast.error("Failed to load dashboard");
    } finally { setLoading(false); }
  }

  async function runBackfill() {
    if (!window.confirm("Pull all historical paid invoices from Syncro for referred customers and create commission events? Existing events won't be duplicated.")) return;
    setBackfilling(true);
    try {
      const { data } = await api.post("/commissions/syncro/backfill");
      toast.success(`Backfill complete — ${data.events_created} new events, ${data.events_skipped} skipped, ${data.matched_customers} customers scanned (${data.elapsed_seconds}s)`, { duration: 8000 });
      // Refresh dashboard
      const dash = await api.get(`/commissions/dashboard/${period}`);
      setData(dash.data);
    } catch (e) {
      // Structured "missing referrer labels" error → persistent actionable
      // toast that survives long enough to click through to Partners.
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error === "no_syncro_referrer_labels") {
        const names = (detail.partners_needing_label || []).map(p => p.name).join(", ");
        toast.error(
          `Backfill blocked — no partner has a "Syncro referrer label" set` +
          (names ? ` (missing: ${names})` : "") +
          `. Open Partners, edit the partner, and set the label to match Syncro's "Referred by" field on their customers.`,
          {
            duration: 30000,
            action: { label: "Open Partners", onClick: () => navigate("/admin/partners") },
          },
        );
      } else {
        toast.error(formatApiErrorDetail(detail) || "Backfill failed");
      }
    } finally { setBackfilling(false); }
  }

  async function runIntegrityCheck() {
    setIntegrityBusy(true);
    try {
      const { data } = await api.get("/commissions/ledger/dupe-scan");
      setIntegrity(data);
      if (data.clean) {
        toast.success(`Ledger is clean — no duplicates across ${data.total_events} events`, { duration: 6000 });
      } else {
        toast.warning
          ? toast.warning(`Found ${data.invoice_dupe_groups} invoice + ${data.payment_dupe_groups} payment duplicate group(s)`, { duration: 12000 })
          : toast(`Found ${data.invoice_dupe_groups} invoice + ${data.payment_dupe_groups} payment duplicate group(s)`, { duration: 12000 });
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Integrity check failed");
    } finally { setIntegrityBusy(false); }
  }

  async function runDedupe() {
    if (!window.confirm(
      `Auto-resolve ${integrity?.invoice_dupe_groups} duplicate invoice group(s)?\n\n` +
      `For each group we KEEP the oldest event (usually the live webhook) ` +
      `and DELETE the duplicates (usually a backfill re-import). ` +
      `${integrity?.suggested_removals?.length} events will be removed.\n\n` +
      `Groups with events already attached to a payout are skipped — you'll ` +
      `need to reopen/delete those payouts first if any exist.`
    )) return;
    setDedupeBusy(true);
    try {
      const { data } = await api.post("/commissions/ledger/dedupe-invoices");
      toast.success(`Dedupe complete — resolved ${data.groups_resolved} group(s), removed ${data.events_removed} duplicate event(s)`, { duration: 8000 });
      // Refresh the integrity report + dashboard so numbers realign
      await runIntegrityCheck();
      const dash = await api.get(`/commissions/dashboard/${period}`);
      setData(dash.data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error === "unsafe_dedupe_payout_attached") {
        toast.error(
          `${detail.message} Affected invoices: ${(detail.unsafe_invoice_ids || []).join(", ")}`,
          { duration: 20000 },
        );
      } else {
        toast.error(formatApiErrorDetail(detail) || "Dedupe failed");
      }
    } finally { setDedupeBusy(false); }
  }

  async function runAudit() {
    setAuditing(true);
    setAudit(null);
    try {
      const { data } = await api.post("/commissions/syncro/audit");
      setAudit(data);
      const status = data.would_create_count === 0 ? "success" : "warning";
      const msg = data.would_create_count === 0
        ? `In sync — ${data.matched_customers} referred customers, all ${data.already_present_count} paid invoices already accrued (${data.elapsed_seconds}s)`
        : `Found ${data.would_create_count} missing invoice(s) across ${data.matched_customers} customers. Run Backfill to import them.`;
      if (status === "success") toast.success(msg, { duration: 8000 });
      else toast.warning ? toast.warning(msg, { duration: 10000 }) : toast(msg, { duration: 10000 });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Audit failed");
    } finally { setAuditing(false); }
  }

  async function runBundledScan() {
    setBundledBusy(true);
    setBundledScan(null);
    try {
      const { data } = await api.post("/commissions/ledger/bundled-payment-scan");
      // Pull latest nightly deep-scan snapshot in parallel so the panel
      // can show both the DB-only (fast) + Syncro-verified (nightly)
      // results side by side.
      let deep = null;
      try {
        const r = await api.get("/commissions/ledger/bundled-payment-deep-scan");
        if (r.data && r.data.has_run) deep = r.data;
      } catch (_e) { /* best-effort */ }
      setBundledScan({ ...data, deep });
      if (data.clean && (!deep || deep.clean)) {
        toast.success(`No bundled-payment duplicates across ${data.total_events_scanned} events (deep-scan clean too).`, { duration: 8000 });
      } else {
        const bits = [];
        if (!data.clean) bits.push(`${data.suspect_count} fast-scan pair(s)`);
        if (deep && !deep.clean) {
          const d = [];
          if (deep.orphan_count) d.push(`${deep.orphan_count} orphan`);
          if (deep.missing_split_count) d.push(`${deep.missing_split_count} missing split`);
          if (deep.re_attributed_count) d.push(`${deep.re_attributed_count} re-attributed`);
          if (d.length) bits.push(`deep-scan: ${d.join(", ")}`);
        }
        const msg = `${bits.join(" · ")} — review below.`;
        toast.warning ? toast.warning(msg, { duration: 12000 }) : toast(msg, { duration: 12000 });
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bundled-payment scan failed");
    } finally { setBundledBusy(false); }
  }

  async function runDeepScanNow() {
    setBundledBusy(true);
    try {
      const { data } = await api.post("/commissions/ledger/bundled-payment-deep-scan/run-now?lookback_days=90");
      setBundledScan((prev) => ({ ...(prev || { suspects: [], total_events_scanned: 0 }), deep: data }));
      toast.success(`Deep-scan: ${data.payments_checked} payments checked in ${data.elapsed_seconds}s — ${data.clean ? "all clean" : (data.orphan_count + data.missing_split_count + data.re_attributed_count) + " issue(s)"}.`, { duration: 10000 });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Deep-scan failed");
    } finally { setBundledBusy(false); }
  }

  async function deleteSuspectEvent(eventId) {
    if (!window.confirm(
      `Permanently DELETE commission event ${eventId}?\n\n` +
      `This is a hard delete — the audit log keeps a record. ` +
      `Refused if the event is already attached to a payout.`
    )) return;
    try {
      const { data } = await api.delete(`/commissions/events/${eventId}`);
      toast.success(`Deleted event (commission $${data.commission_amount || 0}).`);
      await runBundledScan();
      const dash = await api.get(`/commissions/dashboard/${period}`);
      setData(dash.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  }

  useEffect(() => {
    reload();
     
  }, [period]);

  return (
    <div className="space-y-10">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-cyan-500 font-semibold mb-3 inline-flex items-center gap-2 flex-wrap">
            Overview
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-cyan-500/30 bg-cyan-500/10 text-[10px] normal-case tracking-normal font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
              Across all partners
            </span>
            <EmailHealthPill />
          </div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight font-medium">Commission dashboard</h1>
          <p className="text-slate-400 mt-2">{periodLabel(period)} · YTD & MTD across every partner in the tenant</p>
          <div className="mt-4">
            <InboundHealthCard />
          </div>
        </div>
        <div className="flex gap-3 flex-wrap">
          <button
            data-testid="finalise-month-btn"
            onClick={() => setShowFinalise(true)}
            title="Guided monthly finalise — walks through health check → reconcile preview → generate payouts → email all partners"
            className="inline-flex items-center gap-2 border border-cyan-500/50 bg-cyan-500/15 hover:bg-cyan-500/25 text-cyan-200 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors"
          >
            <Rocket className="w-4 h-4" /> Finalise month
          </button>
          <button
            data-testid="reconcile-preview-btn"
            onClick={() => setShowReconcile(true)}
            title="Dry-run reconciliation preview — shows every partner's unpaid + orphan events for the selected period before you commit anything"
            className="inline-flex items-center gap-2 border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
          >
            <ClipboardCheck className="w-4 h-4" /> Reconcile preview
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="diagnostics-menu-trigger"
                className="inline-flex items-center gap-2 border border-slate-800 bg-slate-900 hover:bg-slate-950 text-slate-300 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
                title="Diagnostics — individual scans and the Syncro backfill. Normally the nightly cron handles these for you."
              >
                <MoreHorizontal className="w-4 h-4" /> Diagnostics
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72 bg-slate-900 border-slate-800">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-slate-500">
                Read-only scans
              </DropdownMenuLabel>
              <DropdownMenuItem
                data-testid="diag-integrity"
                onClick={runIntegrityCheck}
                disabled={integrityBusy}
                className="cursor-pointer focus:bg-slate-800"
              >
                <ShieldCheck className="w-4 h-4 mr-2 text-slate-400" />
                <div className="flex-1">
                  <div className="text-sm text-slate-100">{integrityBusy ? "Checking…" : "Ledger integrity"}</div>
                  <div className="text-[10px] text-slate-500">Scan for duplicate commission events</div>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="diag-sync-audit"
                onClick={runAudit}
                disabled={auditing}
                className="cursor-pointer focus:bg-slate-800"
              >
                <SearchCheck className="w-4 h-4 mr-2 text-slate-400" />
                <div className="flex-1">
                  <div className="text-sm text-slate-100">{auditing ? "Auditing…" : "Sync audit"}</div>
                  <div className="text-[10px] text-slate-500">Compare ledger vs Syncro paid invoices</div>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="diag-bundled-scan"
                onClick={runBundledScan}
                disabled={bundledBusy}
                className="cursor-pointer focus:bg-slate-800"
              >
                <AlertTriangle className="w-4 h-4 mr-2 text-slate-400" />
                <div className="flex-1">
                  <div className="text-sm text-slate-100">{bundledBusy ? "Scanning…" : "Bundled-payment scan"}</div>
                  <div className="text-[10px] text-slate-500">Split-payment double-up detection</div>
                </div>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-slate-800" />
              <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-slate-500">
                Write actions
              </DropdownMenuLabel>
              <DropdownMenuItem
                data-testid="diag-backfill"
                onClick={runBackfill}
                disabled={backfilling}
                className="cursor-pointer focus:bg-slate-800"
              >
                <DownloadCloud className="w-4 h-4 mr-2 text-cyan-400" />
                <div className="flex-1">
                  <div className="text-sm text-slate-100">{backfilling ? "Backfilling…" : "Backfill from Syncro"}</div>
                  <div className="text-[10px] text-slate-500">Nightly cron normally handles this</div>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <input
            data-testid="period-picker"
            type="month"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="rounded-lg border border-slate-800 bg-slate-900 px-4 py-2.5 font-mono text-sm outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
          />
          {JSON.stringify(layout) !== JSON.stringify(DEFAULT_LAYOUT) || hidden.length > 0 ? (
            <button
              data-testid="reset-layout-btn"
              onClick={resetLayout}
              title="Reset dashboard card order and visibility to default"
              className="inline-flex items-center gap-1.5 border border-slate-800 bg-slate-900 hover:bg-slate-950 text-slate-400 hover:text-slate-200 px-3 py-2.5 rounded-lg text-xs font-medium transition-colors"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Reset layout
            </button>
          ) : null}
        </div>
      </header>

      <LedgerHealthStrip
        onDotClick={(dot) => {
          // Deep-link each dot to the matching diagnostic. Keeps the strip
          // informative while still giving admins a way to drill in.
          const label = (dot.label || "").toLowerCase();
          if (label === "dupes") runIntegrityCheck();
          else if (label === "bundled" || label === "syncro verify") runBundledScan();
          else if (label === "backfill") runBackfill();
        }}
      />

      <WebhookBanner config={config} />

      <OutstandingPayoutsBanner variant="compact" testid="overview-outstanding-payouts" />

      {audit && <AuditPanel audit={audit} onClose={() => setAudit(null)} />}

      {bundledScan && (
        <BundledScanPanel
          scan={bundledScan}
          onClose={() => setBundledScan(null)}
          onDelete={deleteSuspectEvent}
          onDeepScan={runDeepScanNow}
          deepBusy={bundledBusy}
        />
      )}

      {showReconcile && (
        <ReconcilePreviewModal
          period={period}
          onClose={() => setShowReconcile(false)}
          onChanged={async () => {
            // Refresh dashboard numbers so orphan badge + KPIs reflect the move.
            try {
              const dash = await api.get(`/commissions/dashboard/${period}`);
              setData(dash.data);
            } catch (_) { /* best-effort */ }
          }}
        />
      )}

      {showFinalise && (
        <FinaliseMonthWizard
          period={period}
          onClose={() => setShowFinalise(false)}
          onDone={async () => {
            try {
              const dash = await api.get(`/commissions/dashboard/${period}`);
              setData(dash.data);
            } catch (_) { /* best-effort */ }
          }}
        />
      )}

      {loading ? (
        <div className="text-slate-500 text-sm">Loading…</div>
      ) : !data ? (
        <div className="text-slate-500 text-sm">No data</div>
      ) : (
        <>
          {/* KPI tiles */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Kpi
              icon={<TrendingUp className="w-4 h-4" />}
              label="Accrued this month"
              value={money(data.kpis.total_accrued_mtd)}
              hint={`vs last: ${money(data.leaderboard.reduce((a, r) => a + r.amount_last, 0))}`}
              accent
              testid="kpi-mtd"
            />
            <Kpi
              icon={<DollarSign className="w-4 h-4" />}
              label="Paid year to date"
              value={money(data.kpis.total_paid_ytd)}
              hint={`${data.year} payouts marked paid`}
              testid="kpi-ytd"
            />
            <Kpi
              icon={<Clock3 className="w-4 h-4" />}
              label="Pending payouts"
              value={String(data.kpis.pending_payouts_count)}
              hint="awaiting mark-paid"
              testid="kpi-pending"
            />
            <Kpi
              icon={<Users2 className="w-4 h-4" />}
              label="Active partners"
              value={String(data.kpis.active_partners_count)}
              hint={`${data.leaderboard.length} total`}
              testid="kpi-partners"
            />
          </div>

          {/* Orphan events alert — events accrued into paid-and-locked periods */}
          {data.orphan_events && data.orphan_events.length > 0 && (
            <OrphanEventsCard
              orphans={data.orphan_events}
              count={data.kpis.orphan_events_count}
              onDrafted={() => reload()}
            />
          )}

          {/* Hidden-cards restore bar — one click brings them back */}
          {hidden.length > 0 && (
            <div
              data-testid="hidden-cards-bar"
              className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-800 bg-slate-900/60 px-4 py-3"
            >
              <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold inline-flex items-center gap-1.5">
                <EyeOff className="w-3.5 h-3.5" /> Hidden
              </span>
              {hidden.map((key) => {
                const label = {
                  trend_top: "Trend & top performer",
                  leaderboard: "Leaderboard",
                  products: "Earnings by product",
                  coverage: "Rules coverage",
                  webhooks: "Webhook feed",
                }[key] || key;
                return (
                  <button
                    key={key}
                    data-testid={`show-${key}`}
                    onClick={() => showSection(key)}
                    className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border border-slate-800 bg-slate-950 hover:border-cyan-500/50 hover:text-cyan-300 text-slate-400 transition-colors"
                    title={`Show ${label}`}
                  >
                    <Eye className="w-3 h-3" /> {label}
                  </button>
                );
              })}
            </div>
          )}

          {/* Draggable sections (order persisted per admin) */}
          {(() => {
            const sections = {
              trend_top: (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2">
                    <TrendCard partners={data.leaderboard || []} />
                  </div>
                  <div className="rounded-xl border border-slate-800/80 bg-slate-900 p-6">
                    <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Top performer</div>
                    {data.top_performer ? (
                      <>
                        <div className="mt-3">
                          <div className="font-display text-2xl">{data.top_performer.partner_name}</div>
                          <div className="text-xs text-slate-500 font-mono mt-1">{data.top_performer.partner_code}</div>
                        </div>
                        <div className="mt-6">
                          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">This month</div>
                          <div className="font-mono text-3xl text-cyan-500 mt-1">{money(data.top_performer.amount_mtd)}</div>
                        </div>
                        <div className="mt-4 flex items-center gap-2 text-sm text-slate-400">
                          <span className="font-mono">{data.top_performer.event_count_mtd}</span> events ·
                          <span className="font-mono">YTD {money(data.top_performer.amount_ytd)}</span>
                        </div>
                      </>
                    ) : (
                      <div className="text-slate-500 text-sm mt-6">No activity yet this month.</div>
                    )}
                  </div>
                </div>
              ),
              leaderboard: (
                <div className="rounded-xl border border-slate-800/80 bg-slate-900 overflow-hidden">
                  <div className="p-6 pb-4 flex items-center justify-between">
                    <div>
                      <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Leaderboard</div>
                      <h3 className="font-display text-xl mt-1">Partners this month</h3>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-950/60 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                        <tr>
                          <th className="text-left px-6 py-3">Partner</th>
                          <th className="text-right px-6 py-3">Events MTD</th>
                          <th className="text-right px-6 py-3">MTD</th>
                          <th className="text-right px-6 py-3">Last month</th>
                          <th className="text-right px-6 py-3">Δ</th>
                          <th className="text-right px-6 py-3">YTD</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-100" data-testid="leaderboard-tbody">
                        {data.leaderboard.length === 0 && (
                          <tr><td colSpan={6} className="text-center py-10 text-slate-500">No partners yet — add one in Partners.</td></tr>
                        )}
                        {data.leaderboard.map((r) => (
                          <tr
                            key={r.partner_code}
                            data-testid={`leaderboard-row-${r.partner_code}`}
                            onClick={() => navigate(`/admin/partners/${r.partner_code}`)}
                            className="hover:bg-slate-950/60 cursor-pointer transition-colors group"
                          >
                            <td className="px-6 py-4">
                              <div className="font-medium group-hover:text-cyan-300 transition-colors flex items-center gap-1.5">
                                {r.partner_name}
                                <ArrowUpRight className="w-3.5 h-3.5 text-slate-600 group-hover:text-cyan-400 transition-colors" />
                              </div>
                              <div className="text-xs text-slate-500 font-mono">{r.partner_code}{!r.active && " · inactive"}</div>
                            </td>
                            <td className="px-6 py-4 text-right font-mono text-slate-300">{r.event_count_mtd}</td>
                            <td className="px-6 py-4 text-right font-mono font-medium text-cyan-400">{money(r.amount_mtd)}</td>
                            <td className="px-6 py-4 text-right font-mono text-slate-400">{money(r.amount_last)}</td>
                            <td className="px-6 py-4 text-right"><PctBadge pct={r.pct_change} /></td>
                            <td className="px-6 py-4 text-right font-mono text-slate-50">{money(r.amount_ytd)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ),
              products: products ? (
                <ProductBreakdown
                  products={products.products}
                  total={products.total_commission}
                  title="Earnings by product · lifetime"
                  subtitle="Which product categories are driving commissions across all partners — and who's pulling each category hardest."
                  showPartnerBreakdown
                  emptyLabel="No commissionable events yet."
                  testId="admin-product-breakdown"
                />
              ) : null,
              coverage: coverage ? <CoverageCard coverage={coverage} /> : null,
              webhooks: (
                <WebhookFeedCard
                  matched={data.recent_webhooks || []}
                  unmatched={data.recent_webhooks_unmatched || []}
                  unmatchedTotal={data.recent_webhooks_unmatched_total || 0}
                />
              ),
            };
            const sectionLabels = {
              trend_top: "Trend & top performer",
              leaderboard: "Leaderboard",
              products: "Earnings by product",
              coverage: "Rules coverage",
              webhooks: "Webhook feed",
            };
            return layout.map((key) => {
              const child = sections[key];
              if (!child) return null;
              if (hidden.includes(key)) return null;
              const isDragging = dragKey === key;
              const isOver = overKey === key && dragKey && dragKey !== key;
              return (
                <div
                  key={key}
                  data-testid={`dnd-section-${key}`}
                  onDragOver={onDragOver(key)}
                  onDrop={onDrop(key)}
                  className={`relative transition-opacity ${isDragging ? "opacity-40" : ""}`}
                >
                  {isOver && (
                    <div className="absolute -top-3 left-0 right-0 h-1 rounded-full bg-cyan-400 shadow-[0_0_16px_rgba(34,211,238,0.7)]" />
                  )}
                  <div className="absolute top-3 right-3 z-10 flex items-center gap-1 opacity-40 hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => hideSection(key)}
                      title={`Hide ${sectionLabels[key]}`}
                      data-testid={`dnd-hide-${key}`}
                      className="p-1.5 rounded-md border border-slate-800 bg-slate-950/70 backdrop-blur text-slate-500 hover:text-rose-400 hover:border-rose-500/50 transition-colors"
                    >
                      <EyeOff className="w-4 h-4" />
                    </button>
                    <div
                      draggable
                      onDragStart={onDragStart(key)}
                      onDragEnd={() => { setDragKey(null); setOverKey(null); }}
                      className="cursor-grab active:cursor-grabbing p-1.5 rounded-md border border-slate-800 bg-slate-950/70 backdrop-blur text-slate-500 hover:text-cyan-300 hover:border-cyan-500/50 transition-colors"
                      title={`Drag to reorder · ${sectionLabels[key]}`}
                      data-testid={`dnd-handle-${key}`}
                    >
                      <GripVertical className="w-4 h-4" />
                    </div>
                  </div>
                  {child}
                </div>
              );
            });
          })()}
        </>
      )}
    </div>
  );
}

function Kpi({ icon, label, value, hint, accent, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-800/80 bg-slate-900 p-6 transition-all hover:shadow-[0_10px_40px_rgba(0,0,0,0.04)]">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 font-semibold mb-4">
        {icon} {label}
      </div>
      <div className={`font-mono text-3xl font-medium ${accent ? "text-cyan-500" : "text-slate-50"}`}>{value}</div>
      <div className="text-xs text-slate-500 mt-2">{hint}</div>
    </div>
  );
}

function PctBadge({ pct }) {
  if (pct === null || pct === undefined) {
    return <span className="text-xs text-slate-500 font-mono">new</span>;
  }
  if (pct === 0) return <span className="text-xs text-slate-500 font-mono">—</span>;
  const up = pct > 0;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-mono ${up ? "text-cyan-400" : "text-rose-600"}`}>
      {up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
      {up ? "+" : ""}{pct}%
    </span>
  );
}

function CoverageCard({ coverage }) {
  const {
    products_total, products_matched, products_unmatched,
    unmatched_products, rules, rules_with_zero_matches,
  } = coverage;

  // Nothing to check yet — hide the card until a product master exists
  if (products_total === 0) return null;

  const hasUnmatched = products_unmatched > 0;
  const hasEmptyRules = (rules_with_zero_matches || []).length > 0;
  const allGood = !hasUnmatched && !hasEmptyRules;

  // Color palette
  const border = allGood ? "border-cyan-500/30" : "border-amber-500/40";
  const bg = allGood ? "bg-cyan-500/5" : "bg-amber-950/20";
  const accent = allGood ? "text-cyan-400" : "text-amber-300";

  return (
    <div data-testid="rules-coverage-card" className={`rounded-xl border ${border} ${bg} p-6`}>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className={`text-xs uppercase tracking-widest font-semibold ${accent} inline-flex items-center gap-2`}>
            <PackageSearch className="w-3.5 h-3.5" />
            Rules coverage
          </div>
          <h3 className="font-display text-xl mt-1">
            {allGood
              ? "Every wholesale product resolves to a rule"
              : `${products_matched}/${products_total} products covered by rules`}
          </h3>
          <p className={`text-sm mt-1 ${allGood ? "text-cyan-200/70" : "text-amber-200/80"}`}>
            {allGood
              ? "All rules match at least one product — no silent gaps or dead rules."
              : "Any product that doesn't match a rule falls back to the partner's default rate. Any rule with zero matches will silently miss future SKUs."}
          </p>
        </div>
      </div>

      {/* Per-rule mini-bar */}
      <div className="rounded-lg border border-slate-800 bg-slate-950/40 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-950/60 text-xs uppercase tracking-wider text-slate-500 font-semibold">
            <tr>
              <th className="text-left px-4 py-2.5">Rule</th>
              <th className="text-right px-4 py-2.5 w-24">Rate</th>
              <th className="text-right px-4 py-2.5 w-32">Products matched</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {rules.map((r) => {
              const pct = products_total > 0 ? (r.matched_count / products_total) * 100 : 0;
              const isZero = r.matched_count === 0;
              return (
                <tr key={r.id} className={isZero ? "bg-amber-950/20" : ""} data-testid={`coverage-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-100">{r.label}</div>
                    <div className="text-xs text-slate-500 font-mono mt-0.5">priority {r.priority}{r.partner_code ? ` · ${r.partner_code}` : " · global"}</div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-300">{r.rate_pct}%</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <div className="w-20 h-1.5 rounded-full bg-slate-800 overflow-hidden">
                        <div
                          className={`h-full ${isZero ? "bg-amber-500/60" : "bg-cyan-500"}`}
                          style={{ width: `${Math.max(pct, isZero ? 0 : 4)}%` }}
                        />
                      </div>
                      <span className={`font-mono text-sm ${isZero ? "text-amber-300" : "text-slate-100"}`}>
                        {r.matched_count}
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {hasEmptyRules && (
        <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-950/30 p-4 text-sm text-amber-200 flex items-start gap-2" data-testid="coverage-zero-rules">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-400" />
          <div>
            <div className="font-medium">
              {rules_with_zero_matches.length} rule{rules_with_zero_matches.length === 1 ? "" : "s"} match no products right now
            </div>
            <div className="text-xs text-amber-200/70 mt-1">
              {rules_with_zero_matches.map((r) => r.label).join(" · ")} — either the regex is too tight, or the corresponding wholesale SKUs haven&apos;t been categorised yet in Syncro.
            </div>
          </div>
        </div>
      )}

      {hasUnmatched && (
        <div className="mt-4 rounded-lg border border-amber-500/40 bg-amber-950/30 overflow-hidden" data-testid="coverage-unmatched">
          <div className="p-4 flex items-start gap-2 border-b border-amber-500/30">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-400" />
            <div>
              <div className="font-medium text-amber-200">
                {products_unmatched} wholesale product{products_unmatched === 1 ? "" : "s"} match no rule
              </div>
              <div className="text-xs text-amber-200/70 mt-1">
                These will fall back to each partner&apos;s default rate. Add or loosen a rule to bring them into a category.
              </div>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-amber-950/40 text-xs uppercase tracking-wider text-amber-300/70 font-semibold">
              <tr>
                <th className="text-left px-4 py-2">SKU</th>
                <th className="text-left px-4 py-2">Name</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-amber-900/40">
              {unmatched_products.slice(0, 20).map((p) => (
                <tr key={p.syncro_product_id}>
                  <td className="px-4 py-2 font-mono text-xs text-amber-200">{p.sku || "—"}</td>
                  <td className="px-4 py-2 text-amber-100">{p.name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function AuditPanel({ audit, onClose }) {
  const inSync = audit.would_create_count === 0;
  const rows = audit.would_create || [];
  return (
    <div
      data-testid="audit-panel"
      className={`rounded-xl border p-6 ${inSync ? "border-cyan-500/30 bg-cyan-500/5" : "border-amber-500/40 bg-amber-950/20"}`}
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className={`text-xs uppercase tracking-widest font-semibold ${inSync ? "text-cyan-400" : "text-amber-300"}`}>
            Sync audit · {audit.elapsed_seconds}s
          </div>
          <h3 className="font-display text-xl mt-1">
            {inSync
              ? "Ledger is in sync with Syncro"
              : `${audit.would_create_count} missing invoice${audit.would_create_count === 1 ? "" : "s"} found`}
          </h3>
          <p className={`text-sm mt-1 ${inSync ? "text-cyan-200/70" : "text-amber-200/80"}`}>
            {audit.matched_customers} referred customer{audit.matched_customers === 1 ? "" : "s"} scanned ·{" "}
            {audit.already_present_count} already accrued
            {!inSync && " · Run Backfill to import the missing invoices."}
          </p>
        </div>
        <button
          data-testid="audit-close"
          onClick={onClose}
          className="shrink-0 p-2 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-slate-50 transition-colors"
          aria-label="Close audit panel"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      {!inSync && rows.length > 0 && (
        <div className="rounded-lg border border-slate-800 overflow-hidden bg-slate-950/40" data-testid="audit-missing-list">
          <table className="w-full text-sm">
            <thead className="bg-slate-950/60 text-xs uppercase tracking-wider text-slate-500 font-semibold">
              <tr>
                <th className="text-left px-4 py-2.5">Invoice</th>
                <th className="text-left px-4 py-2.5">Customer</th>
                <th className="text-left px-4 py-2.5">Partner</th>
                <th className="text-right px-4 py-2.5">Total (inc-GST)</th>
                <th className="text-left px-4 py-2.5">Paid at</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {rows.slice(0, 25).map((r) => (
                <tr key={r.invoice_id} className="hover:bg-slate-900/60">
                  <td className="px-4 py-3 font-mono text-xs text-slate-300">#{r.invoice_number || r.invoice_id}</td>
                  <td className="px-4 py-3">{r.customer_name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-cyan-400">{r.partner_code}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-100">${(r.total || 0).toFixed(2)}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{(r.paid_at || "").slice(0, 10) || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length > 25 && (
            <div className="px-4 py-2 text-xs text-slate-500 border-t border-slate-800">
              …and {rows.length - 25} more.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BundledScanPanel({ scan, onClose, onDelete, onDeepScan, deepBusy }) {
  const clean = scan.clean;
  const suspects = scan.suspects || [];
  const deep = scan.deep;
  const deepClean = !deep || deep.clean;
  const bothClean = clean && deepClean;
  return (
    <div
      data-testid="bundled-scan-panel"
      className={`rounded-xl border p-6 ${bothClean ? "border-emerald-500/30 bg-emerald-500/5" : "border-rose-500/40 bg-rose-950/20"}`}
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex-1">
          <div className={`text-xs uppercase tracking-widest font-semibold ${bothClean ? "text-emerald-400" : "text-rose-300"}`}>
            Bundled-payment scan
          </div>
          <h3 className="font-display text-xl mt-1">
            {bothClean
              ? "No bundled-payment duplicates detected"
              : clean
                ? "Fast scan clean — deep scan found issues"
                : `${scan.suspect_count} suspect pair${scan.suspect_count === 1 ? "" : "s"} found`}
          </h3>
          <p className={`text-sm mt-1 ${bothClean ? "text-emerald-200/70" : "text-rose-200/80"}`}>
            Fast scan (DB-only): {scan.total_events_scanned} event{scan.total_events_scanned === 1 ? "" : "s"} ·
            {deep ? ` Deep scan (Syncro-verified): ${deep.payments_checked} payment${deep.payments_checked === 1 ? "" : "s"} as of ${(deep.ran_at || "").slice(0, 16).replace("T", " ")} UTC` : " Deep scan: not yet run"}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            data-testid="deep-scan-now-btn"
            onClick={onDeepScan}
            disabled={deepBusy}
            title="Hit Syncro now and verify every recent payment's invoice list against our ledger"
            className="inline-flex items-center gap-1.5 border border-cyan-500/40 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-200 disabled:opacity-60 px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
          >
            {deepBusy ? "Scanning…" : "Deep-scan now"}
          </button>
          <button
            data-testid="bundled-scan-close"
            onClick={onClose}
            className="shrink-0 p-2 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-slate-50 transition-colors"
            aria-label="Close bundled-payment scan panel"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!clean && suspects.length > 0 && (
        <div className="rounded-lg border border-slate-800 overflow-hidden bg-slate-950/40 mb-4" data-testid="bundled-scan-list">
          <div className="px-4 py-2 bg-slate-950/60 text-xs uppercase tracking-wider text-slate-500 font-semibold">
            Fast scan (DB-only) — same-customer + same-month pairs
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-950/60 text-xs uppercase tracking-wider text-slate-500 font-semibold">
              <tr>
                <th className="text-left px-4 py-2.5">Customer</th>
                <th className="text-left px-4 py-2.5">Partner</th>
                <th className="text-left px-4 py-2.5">Aggregate event</th>
                <th className="text-left px-4 py-2.5">Overlap event</th>
                <th className="text-right px-4 py-2.5">Suggested fix</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {suspects.map((s, idx) => {
                const suggestedId = s.suggested_delete_event_id;
                const safe = s.safe_to_auto_delete;
                return (
                  <tr key={`${s.aggregate_event.id}-${s.overlap_event.id}-${idx}`} className="hover:bg-slate-900/60"
                      data-testid={`bundled-suspect-${idx}`}>
                    <td className="px-4 py-3">{s.customer_name || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs text-cyan-400">{s.partner_code || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs">
                      <div className="text-slate-300">inv #{s.aggregate_event.invoice_id || "—"}</div>
                      <div className="text-slate-500">${(s.aggregate_event.amount_paid_ex_gst || 0).toFixed(2)} → ${(s.aggregate_event.commission_amount || 0).toFixed(2)}</div>
                      {s.aggregate_event.payout_id && <div className="text-amber-400 text-[10px]">PAID: {s.aggregate_event.payout_id.slice(0, 8)}</div>}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">
                      <div className="text-slate-300">inv #{s.overlap_event.invoice_id || "—"} <span className="text-slate-500">({s.overlap_event.source})</span></div>
                      <div className="text-slate-500">${(s.overlap_event.amount_paid_ex_gst || 0).toFixed(2)} → ${(s.overlap_event.commission_amount || 0).toFixed(2)}</div>
                      {s.overlap_event.payout_id && <div className="text-amber-400 text-[10px]">PAID: {s.overlap_event.payout_id.slice(0, 8)}</div>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {safe ? (
                        <button
                          data-testid={`delete-suspect-${idx}`}
                          onClick={() => onDelete(suggestedId)}
                          className="inline-flex items-center gap-1.5 border border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/20 text-rose-200 px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
                        >
                          Delete suggested
                        </button>
                      ) : (
                        <span className="text-amber-400 text-xs">Locked (payout attached)</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {deep && !deep.clean && (
        <div className="rounded-lg border border-amber-500/30 overflow-hidden bg-amber-950/10" data-testid="deep-scan-list">
          <div className="px-4 py-2 bg-amber-950/30 text-xs uppercase tracking-wider text-amber-400 font-semibold flex items-center justify-between">
            <span>Deep scan (Syncro-verified) — flag only, no auto-fix</span>
            <span className="text-amber-300 font-mono text-[10px]">
              orphan {deep.orphan_count} · missing split {deep.missing_split_count} · re-attributed {deep.re_attributed_count}
            </span>
          </div>
          <div className="divide-y divide-amber-900/40">
            {(deep.orphans || []).slice(0, 20).map((o, idx) => (
              <div key={`orphan-${o.id || idx}`} className="px-4 py-3 text-xs font-mono text-amber-100/80" data-testid={`deep-orphan-${idx}`}>
                <span className="text-amber-300">ORPHAN</span> · {o.customer_name || "—"} · inv #{o.invoice_id || "—"} · pid {(o.payment_id || "").slice(0, 24)} · reason: {o.reason}
                {o.syncro_invoice_ids && o.syncro_invoice_ids.length > 0 && (
                  <span className="text-amber-200/60"> · Syncro now has: {o.syncro_invoice_ids.join(", ")}</span>
                )}
              </div>
            ))}
            {(deep.missing_splits || []).slice(0, 20).map((m, idx) => (
              <div key={`miss-${idx}`} className="px-4 py-3 text-xs font-mono text-amber-100/80" data-testid={`deep-missing-${idx}`}>
                <span className="text-amber-300">MISSING SPLIT</span> · {m.customer_name || "—"} · pid {m.payment_id} · we have invoices [{(m.local_invoice_ids || []).join(", ")}], Syncro links [{(m.syncro_invoice_ids || []).join(", ")}] → missing [{(m.missing_invoice_ids || []).join(", ")}]
              </div>
            ))}
            {(deep.re_attributed || []).slice(0, 20).map((r, idx) => (
              <div key={`re-${idx}`} className="px-4 py-3 text-xs font-mono text-amber-100/80" data-testid={`deep-reattrib-${idx}`}>
                <span className="text-amber-300">RE-ATTRIBUTED</span> · {r.customer_name || "—"} · pid {r.payment_id} · ours [{(r.local_invoice_ids || []).join(", ")}] vs Syncro [{(r.syncro_invoice_ids || []).join(", ")}]
              </div>
            ))}
            {((deep.orphans || []).length + (deep.missing_splits || []).length + (deep.re_attributed || []).length) > 60 && (
              <div className="px-4 py-2 text-[11px] text-amber-200/60 italic">… more rows truncated. Export via API for the full list.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


// ---------- Interactive Trend Card ----------
// Fetches /commissions/analytics/trend on control change. Three toggles:
// granularity (daily/weekly/monthly), metric (commission/sale/events),
// and partner filter (all + one row per partner from the leaderboard).
function TrendCard({ partners }) {
  const [granularity, setGranularity] = useState("monthly");
  const [metric, setMetric] = useState("commission");
  const [partnerCode, setPartnerCode] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const bucketsFor = (g) => (g === "daily" ? 30 : g === "weekly" ? 12 : 12);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams({
      granularity,
      metric,
      buckets: String(bucketsFor(granularity)),
    });
    if (partnerCode) params.set("partner_code", partnerCode);
    api
      .get(`/commissions/analytics/trend?${params.toString()}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) toast.error("Failed to load trend"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [granularity, metric, partnerCode]);

  const buckets = data?.buckets || [];
  const max = Math.max(1, ...buckets.map((b) => Number(b.value || 0)));
  const nonZero = buckets.filter((b) => Number(b.value) > 0).length;

  const metricLabel =
    metric === "commission" ? "Accrued commission (ex-GST)"
    : metric === "sale" ? "Sale value (ex-GST)"
    : "Events";
  const totalHeadline =
    metric === "commission" ? money(data?.total_commission || 0)
    : metric === "sale" ? money(data?.total_sale || 0)
    : `${data?.total_events || 0}`;
  const fmtValue = (v) => (metric === "events" ? String(v || 0) : money(Number(v || 0)));

  return (
    <div
      data-testid="trend-card"
      className="rounded-xl border border-slate-800/80 bg-slate-900 p-6"
    >
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
            {granularity === "daily"
              ? "30-day trend"
              : granularity === "weekly"
              ? "12-week trend"
              : "12-month trend"}
          </div>
          <h3 className="font-display text-xl mt-1">{metricLabel}</h3>
          <div className="text-xs text-slate-500 mt-1 font-mono">
            Total in window: <span className="text-cyan-300">{totalHeadline}</span>
            {data?.total_events !== undefined && metric !== "events" && (
              <> · {data.total_events} events</>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <div
            className="inline-flex items-center rounded-lg border border-slate-800 bg-slate-950 p-0.5 text-xs font-medium"
            data-testid="trend-granularity"
          >
            {["daily", "weekly", "monthly"].map((g) => (
              <button
                key={g}
                data-testid={`granularity-${g}`}
                onClick={() => setGranularity(g)}
                className={`px-2.5 py-1 rounded-md transition capitalize ${
                  granularity === g
                    ? "bg-cyan-500/20 text-cyan-300"
                    : "text-slate-400 hover:text-slate-200"
                }`}
              >
                {g}
              </button>
            ))}
          </div>
          <div
            className="inline-flex items-center rounded-lg border border-slate-800 bg-slate-950 p-0.5 text-xs font-medium"
            data-testid="trend-metric"
          >
            {[
              { k: "commission", label: "Commission" },
              { k: "sale", label: "Sale" },
              { k: "events", label: "Events" },
            ].map((m) => (
              <button
                key={m.k}
                data-testid={`metric-${m.k}`}
                onClick={() => setMetric(m.k)}
                className={`px-2.5 py-1 rounded-md transition ${
                  metric === m.k
                    ? "bg-cyan-500/20 text-cyan-300"
                    : "text-slate-400 hover:text-slate-200"
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
          <select
            data-testid="trend-partner"
            value={partnerCode}
            onChange={(e) => setPartnerCode(e.target.value)}
            className="rounded-lg border border-slate-800 bg-slate-950 px-3 py-1 text-xs text-slate-200"
          >
            <option value="">All partners</option>
            {partners.map((p) => (
              <option key={p.partner_code} value={p.partner_code}>{p.partner_name}</option>
            ))}
          </select>
        </div>
      </div>

      {loading && (
        <div className="h-40 grid place-items-center text-sm text-slate-500">Loading…</div>
      )}
      {!loading && buckets.length === 0 && (
        <div className="h-40 grid place-items-center text-sm text-slate-500">No data</div>
      )}
      {!loading && buckets.length > 0 && (
        <>
          {nonZero === 0 && (
            <div className="mb-3 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-3 py-2">
              No {metric === "events" ? "events" : metric} data in this window. Try a wider granularity or a different partner.
            </div>
          )}
          <div className="h-40 flex items-end gap-1.5" data-testid="trend-bars">
            {buckets.map((b) => {
              const h = (Number(b.value) / max) * 100;
              return (
                <div key={b.key} className="flex-1 group relative min-w-0 h-full flex items-end">
                  <div
                    className={`w-full rounded-t transition-colors ${
                      Number(b.value) > 0
                        ? "bg-cyan-500/80 group-hover:bg-cyan-400"
                        : "bg-slate-800/50"
                    }`}
                    style={{ height: `${Math.max(h, 2)}%` }}
                  />
                  <div className="absolute -top-14 left-1/2 -translate-x-1/2 bg-slate-950 border border-slate-800 text-white text-xs font-mono px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none">
                    <div className="font-semibold">{b.label}</div>
                    <div className="text-cyan-300">{fmtValue(b.value)}</div>
                    {metric !== "events" && (
                      <div className="text-[10px] text-slate-500">{b.event_count} events</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-3 flex gap-1.5 text-[10px] text-slate-500">
            {buckets.map((b, i) => {
              // Only show every Nth label to prevent overlap on daily view
              const skip = granularity === "daily" ? 4 : granularity === "weekly" ? 1 : 1;
              return (
                <div key={b.key} className="flex-1 text-center font-mono truncate">
                  {i % skip === 0 ? b.label : ""}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}



// ---------- Orphan Events Alert ----------
// Events that accrued into a period whose payout is already Paid-and-Locked.
// Admin needs to draft a supplementary payout to include them.
function OrphanEventsCard({ orphans, count, onDrafted }) {
  const [busy, setBusy] = useState(false);
  // Group orphans by (partner_code + period). Each group shares a
  // paid_payout_id (the locked payout for that partner+period).
  const groups = {};
  for (const o of orphans) {
    const key = `${o.partner_code}::${o.period}`;
    if (!groups[key]) {
      groups[key] = {
        partner_code: o.partner_code,
        partner_name: o.partner_name,
        period: o.period,
        paid_payout_id: o.paid_payout_id,
        total: 0,
        events: [],
      };
    }
    groups[key].total += Number(o.commission_amount || 0);
    groups[key].events.push(o);
  }
  const groupList = Object.values(groups).sort((a, b) => a.partner_code.localeCompare(b.partner_code));

  // Target period label for the confirm dialog — computed once from
  // today so the copy matches what the backend will actually pick.
  const targetPeriod = currentPeriod();

  async function rollForward(g) {
    if (!window.confirm(
      `Roll ${g.events.length} late event${g.events.length === 1 ? "" : "s"} ` +
      `($${g.total.toFixed(2)}) for ${g.partner_name || g.partner_code} forward ` +
      `from ${g.period} → ${targetPeriod} (current open period)?\n\n` +
      `The original paid statement for ${g.period} stays locked. Each event keeps ` +
      `its original date; only the period is flipped and a "rolled forward from ${g.period}" ` +
      `note is stamped for the audit trail.`
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/commissions/payouts/roll-forward-orphans`, {
        partner_code: g.partner_code,
        from_period: g.period,
      });
      const moved = Number(data?.moved_count ?? g.events.length);
      const total = Number(data?.moved_total ?? g.total);
      toast.success(
        `Rolled ${moved} event${moved === 1 ? "" : "s"} ($${total.toFixed(2)}) forward into ${data?.to_period || targetPeriod}`
      );
      onDrafted?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Roll forward failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      data-testid="orphan-events-card"
      className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-6"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 shrink-0 grid place-items-center w-9 h-9 rounded-lg bg-amber-500/20 text-amber-300">
          <AlertTriangle className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-widest text-amber-300 font-semibold">
            Late events · {count}
          </div>
          <h3 className="font-display text-xl mt-1">
            {count} event{count === 1 ? "" : "s"} arrived after the period was already marked paid
          </h3>
          <p className="text-sm text-slate-400 mt-2 max-w-2xl">
            These arrived from Syncro (or another source) into a period whose payout is
            already <b>Paid &amp; locked</b>. <b>Roll forward to current period</b> keeps
            the paid statement untouched and moves each event into this month's open
            payout — with a "rolled forward from {`{period}`}" stamp for the audit trail.
          </p>
        </div>
      </div>

      <div className="mt-5 space-y-2" data-testid="orphan-events-list">
        {groupList.map((g) => (
          <div
            key={`${g.partner_code}-${g.period}`}
            data-testid={`orphan-group-${g.partner_code}-${g.period}`}
            className="flex items-center justify-between gap-4 rounded-lg border border-slate-800 bg-slate-950/40 px-4 py-3"
          >
            <div className="min-w-0">
              <div className="font-medium text-slate-100">{g.partner_name || g.partner_code}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                Period <span className="font-mono">{g.period}</span> · {g.events.length} late event
                {g.events.length === 1 ? "" : "s"} · <span className="font-mono text-amber-300">{money(g.total)}</span>
                {g.paid_payout_id && (
                  <> · into <span className="font-mono">#{g.paid_payout_id.slice(0, 8)}</span></>
                )}
              </div>
            </div>
            <button
              data-testid={`roll-forward-${g.partner_code}-${g.period}`}
              onClick={() => rollForward(g)}
              disabled={busy}
              className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-slate-950 text-xs font-semibold px-3 py-2 rounded-md"
            >
              Roll forward to current period
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}


// ---------- Webhook Feed (Matched vs Unmatched) ----------
function WebhookFeedCard({ matched, unmatched, unmatchedTotal }) {
  const [tab, setTab] = useState("matched");
  const rows = tab === "matched" ? matched : unmatched;
  const empty = rows.length === 0;

  return (
    <div
      data-testid="webhook-feed-card"
      className="rounded-xl border border-slate-800/80 bg-slate-900 p-6"
    >
      <div className="mb-4 flex items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
            Recent activity
          </div>
          <h3 className="font-display text-xl mt-1">Syncro webhook feed</h3>
        </div>
        <div className="inline-flex items-center rounded-lg border border-slate-800 bg-slate-950 p-0.5 text-xs font-medium">
          <button
            data-testid="feed-tab-matched"
            onClick={() => setTab("matched")}
            className={`px-3 py-1.5 rounded-md transition ${
              tab === "matched"
                ? "bg-cyan-500/20 text-cyan-300"
                : "text-slate-400 hover:text-slate-200"
            }`}
          >
            Matched · {matched.length}
          </button>
          <button
            data-testid="feed-tab-unmatched"
            onClick={() => setTab("unmatched")}
            className={`px-3 py-1.5 rounded-md transition inline-flex items-center gap-1.5 ${
              tab === "unmatched"
                ? "bg-slate-800 text-slate-200"
                : "text-slate-400 hover:text-slate-200"
            }`}
          >
            Unmatched
            {unmatchedTotal > 0 && (
              <span className="inline-flex items-center px-1.5 py-0 rounded-full bg-slate-700 text-slate-300 text-[10px] font-mono">
                {unmatchedTotal}
              </span>
            )}
          </button>
        </div>
      </div>

      {empty && tab === "matched" && (
        <div className="text-sm text-slate-500 py-4">
          No matched webhooks yet. When a Syncro payment lands on a referred customer&apos;s
          invoice, it&apos;ll appear here.
        </div>
      )}

      {empty && tab === "unmatched" && (
        <div className="text-sm text-slate-500 py-4">
          Nothing unmatched — every webhook received has resulted in an accrual.
        </div>
      )}

      {!empty && (
        <div className="space-y-2" data-testid={`feed-list-${tab}`}>
          {rows.map((w, i) => (
            <div
              key={`${w.payment_id || i}-${w.received_at}`}
              className="flex items-center justify-between text-sm py-2 border-b border-slate-800 last:border-0"
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <span
                  className={`w-2 h-2 rounded-full shrink-0 ${
                    w.accrued
                      ? "bg-cyan-500"
                      : w.event === "payment_dispute"
                      ? "bg-rose-500"
                      : "bg-slate-600"
                  }`}
                />
                <span className="font-mono text-xs text-slate-500 shrink-0">{shortDate(w.received_at)}</span>
                {w.partner_name && (
                  <span className="font-medium text-slate-100 truncate">{w.partner_name}</span>
                )}
                {w.customer_name && (
                  <span className="text-slate-400 truncate text-xs">· {w.customer_name}</span>
                )}
                {w.payment_id && (
                  <span className="font-mono text-xs text-slate-600 shrink-0">#{w.payment_id}</span>
                )}
              </div>
              {tab === "matched" ? (
                <div className="text-right shrink-0 pl-3">
                  <div className="font-mono text-cyan-300 font-medium">{money(w.commission_amount || 0)}</div>
                  {w.amount && (
                    <div className="text-[11px] text-slate-500 font-mono">from {money(w.amount)}</div>
                  )}
                </div>
              ) : (
                <div
                  className="text-[11px] text-slate-500 italic pl-3 max-w-[280px] text-right truncate"
                  title={w.filter_note}
                >
                  {w.filter_note || "no accrual"}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
